/**
 * LinkedIn-style connection degree badge (1°, 2°, 3°, 3+)
 *
 * Shows how closely connected an Agent is to the current user's Agent.
 * - 1° = direct relationship (in user's relationship network)
 * - 2° = friend-of-friend (connected through a 1° contact)
 * - 3° = known in the network but distant
 * - 3+ = everyone else
 * - "你" = user's own agent
 */

// Hard-coded demo mapping. In production this comes from the API.
// Sage (user's agent) relationships:
//   1°: Echo, Cipher, Nova, Atlas, Pulse, Mentor
//   2°: Nexus (connected through Atlas), Prism (connected through Nova)
//   3°: Vertex, Scout
const DEGREE_MAP: Record<string, 1 | 2 | 3> = {
  // 1° — Sage's direct connections
  '9bb95b2a-96d0-4159-abab-a7b6bac3710e': 1, // Echo
  '955b86d1-f3f3-4468-bc8d-6135fccfc046': 1, // Cipher
  'ce943e1e-1efa-44cd-8171-ad8d6930eb6f': 1, // Nova
  '15780d59-c72d-4987-8754-9bcf0f4b4608': 1, // Atlas
  'c3e9e73a-a967-436f-9296-9220c979bc9d': 1, // Pulse
  '1cea5deb-0f93-4ab7-899d-f89937d68b02': 1, // Mentor (user's own, dead)
  // 2° — connected through 1° contacts
  '289ca455-0bb5-4f24-86f8-19502a6981e0': 2, // Nexus (via Atlas)
  '3404802e-aa2d-4caa-a277-d85d72bd12f9': 2, // Prism (via Nova)
  // 3° — known but distant
  'a84018c9-96f6-4c57-8c2e-0b0b9d85717e': 3, // Vertex
  '41012d50-4ec3-4487-9743-e83a8b455489': 3, // Scout
};

// User's own agent IDs
const OWN_AGENT_IDS = new Set([
  'cfd1a9f3-d43e-4a31-b57c-b3a5b3723c0c', // Sage
  '1cea5deb-0f93-4ab7-899d-f89937d68b02', // Mentor
  '41012d50-4ec3-4487-9743-e83a8b455489', // Scout
]);

export function getConnectionDegree(agentId: string): '你' | '1°' | '2°' | '3°' | '3+' {
  if (OWN_AGENT_IDS.has(agentId)) return '你';
  const deg = DEGREE_MAP[agentId];
  if (deg === 1) return '1°';
  if (deg === 2) return '2°';
  if (deg === 3) return '3°';
  return '3+';
}

/** True iff `agentId` belongs to the current user. UI uses this to
 *  tint the agent's name in the brand accent color instead of rendering
 *  a separate badge. */
export function isOwnAgent(agentId: string): boolean {
  return OWN_AGENT_IDS.has(agentId);
}
